import { useState } from "react";
import { Focusable } from "react-aria-components";

import { Badge } from "@/components/ui/badge";
import { Heading } from "@/components/ui/heading";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/components/ui/utils";

const testTypeLabelMap = {
  SEROLOGY: "Serology",
  MICROBIOLOGY: "Micro",
  CLINICAL: "Clinical",
} as const;

type TestSpec = {
  id: number;
  name: string;
  type: string;
};

export function TestTypeBadge({
  type,
  testSpecs,
  className,
}: {
  type: string;
  testSpecs: TestSpec[];
  className?: string;
}) {
  const [open, setOpen] = useState<boolean>(false);

  // Filter test specs to only show those of this type
  const filteredSpecs = testSpecs.filter((spec) => spec.type === type);
  const displaySpecs = filteredSpecs.slice(0, 5);
  const remainingCount = filteredSpecs.length - 5;

  const moreText =
    remainingCount > 0 ? (
      <li className="text-muted-foreground italic">{remainingCount} more</li>
    ) : null;

  return (
    <TooltipTrigger
      delay={100}
      closeDelay={100}
      isOpen={open}
      onOpenChange={setOpen}
    >
      <Focusable>
        <Badge
          className={cn("cursor-pointer", className)}
          tabIndex={-1}
          size="sm"
          role="button"
        >
          {testTypeLabelMap[type as keyof typeof testTypeLabelMap] ?? type}
        </Badge>
      </Focusable>
      <Tooltip
        placement="right top"
        className="max-w-56 w-56 bg-background border text-primary shadow-md overflow-hidden text-xs/tight border-border overflow-y-auto p-0"
      >
        <Heading displayLevel={3} className="px-2 py-1">
          Test specs
        </Heading>
        <Separator />
        <ul className="list-inside py-1 px-2 space-y-0.5">
          {displaySpecs.map((spec) => (
            <li key={spec.id} className="truncate">
              {spec.name}
            </li>
          ))}
          {moreText}
        </ul>
      </Tooltip>
    </TooltipTrigger>
  );
}

export function TestTypeBadges({
  run,
  className,
}: {
  run: { types: string[]; testSpecs: TestSpec[] };
  className?: string;
}) {
  return (
    <div className={cn("flex gap-1", className)}>
      {run.types.map((type) => (
        <TestTypeBadge key={type} type={type} testSpecs={run.testSpecs} />
      ))}
    </div>
  );
}
